import type { Locale, PublicRoute } from './siteConfig';
import {
  CONTACT_EMAIL,
  ORG_LEGAL_NAME,
  ORG_LOGO_URL,
  ORG_NAME,
  SITE_URL,
  SOCIAL_PROFILES,
} from './siteConfig';
import { getRouteMeta } from './routeMeta';

// Returns the JSON-LD blocks that should appear in the <head> for a given
// (locale, route). All blocks are validated by the schema.org structure;
// values are derived from siteConfig and routeMeta so they stay in sync with
// the rest of the marketing copy.

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: ORG_NAME,
    legalName: ORG_LEGAL_NAME,
    url: SITE_URL,
    logo: ORG_LOGO_URL,
    sameAs: SOCIAL_PROFILES,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: CONTACT_EMAIL,
        contactType: 'sales',
        areaServed: ['PY', 'MX', 'CO', 'BR', 'AR', 'CL', 'PE', 'UY', 'EC', 'BO'],
        availableLanguage: ['Spanish', 'English', 'Portuguese'],
      },
    ],
  };
}

export function softwareApplicationSchema(locale: Locale) {
  const meta = getRouteMeta(locale, '/');
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: ORG_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: meta.description,
    url: SITE_URL,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description:
        locale === 'es'
          ? 'Piloto sin costo. Modelo comercial por volumen de minutos evaluados.'
          : 'Free pilot. Commercial model by volume of evaluated minutes.',
    },
  };
}

export interface FaqItem {
  question: string;
  answer: string;
}

export function faqPageSchema(items: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

// Pre-built FAQ content per locale, used to render <FAQPage> JSON-LD on the
// home and product pages. Keep these aligned with on-page FAQ copy if/when an
// FAQ section is added — duplicating Q/A across DOM and JSON-LD is what Google
// validates.
export const HOME_FAQS: Record<Locale, FaqItem[]> = {
  es: [
    {
      question: '¿Qué tipos de desvíos de cumplimiento detecta CallCov?',
      answer:
        'Detectores listos para usar: exposición de datos sensibles y disclosures obligatorios. Reglas configurables: amenazas sin fundamento, contacto fuera de horario, contacto a terceros y desvíos de script. Las reglas configurables se implementan junto con tu equipo durante el onboarding.',
    },
    {
      question: '¿Cómo se conecta CallCov a nuestras grabaciones?',
      answer:
        'Se conecta a las grabaciones que ya genera tu operación a través de SFTP, batch por API o archivos comprimidos (ZIP, tar.gz). No requiere cambiar la operación.',
    },
    {
      question: '¿Cuánto tiempo toma ver resultados?',
      answer:
        'El plan piloto entrega resultados medibles en la primera semana. Si no hay impacto, no se avanza.',
    },
    {
      question: '¿Está adaptado a la regulación de mi país?',
      answer:
        'Operamos con marcos regulatorios de SEDECO (Paraguay), CONDUSEF (México), SBS, CMF, ANPD/LGPD (Brasil) y la Ley 2300 (Colombia). Las reglas específicas se configuran con tu equipo de cumplimiento durante el onboarding.',
    },
    {
      question: '¿Cómo es el modelo de precios?',
      answer:
        'Modelo comercial por volumen de minutos evaluados. Sin tarifas ocultas. El piloto inicial es sin costo y sin compromiso.',
    },
  ],
  en: [
    {
      question: 'What kinds of compliance deviations does CallCov detect?',
      answer:
        'Out-of-the-box detectors: sensitive data exposure and mandatory disclosures. Configurable rules: unfounded threats, off-hours contact, third-party contact, and script deviations. Configurable rules are implemented with your team during onboarding.',
    },
    {
      question: 'How does CallCov connect to our recordings?',
      answer:
        'It connects to the recordings your operation already produces via SFTP, API batch upload, or compressed archives (ZIP, tar.gz). No need to change the operation.',
    },
    {
      question: 'How long does it take to see results?',
      answer:
        'The pilot plan delivers measurable results in the first week. If there is no impact, we do not move forward.',
    },
    {
      question: 'Is it adapted to my country’s regulation?',
      answer:
        'We operate with regulatory frameworks from SEDECO (Paraguay), CONDUSEF (Mexico), SBS, CMF, ANPD/LGPD (Brazil), and Law 2300 (Colombia). Country-specific rules are configured with your compliance team during onboarding.',
    },
    {
      question: 'How does pricing work?',
      answer:
        'Commercial model by volume of evaluated minutes. No hidden fees. The initial pilot is free and with no commitment.',
    },
  ],
};
